import React, { useEffect, useState } from 'react';
import { PermIdentity, MailOutline, LockOutlined, EditOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import Section from './Section';
import { countryPhoneInfo, CurrentUser } from 'src/utils/constant';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/new/Button';
import { Spacer } from 'src/components/new/Spacer';
import { EditProfileData, EditableProfile } from './EditProfileData';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import { setCurrentUser } from 'src/features/user/userSlice';
import { askEmailChange, askPasswordReset, editUserProfile } from 'src/features/user/utils';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { getUserProfile } from 'src/features/settings/utils';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { format } from '@react-input/mask';

interface ProfileTabProps {
  currentUser: CurrentUser;
}

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const ProfileTab: React.FC<ProfileTabProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });

  useEffect(() => {
    dispatch(getUserProfile());
  }, [dispatch]);

  const relevantTimeAgo = (date: string | Date = ''): string => {
    const today = new Date();
    const pastDate = new Date(date);

    const diffTime = today.getTime() - pastDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Updated today';
    } else {
      return `Updated ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
  };

  const getDisplayValue = (value?: string | null): string => {
    return value && value.trim() !== '' ? value : 'Not Specified';
  };

  const getDirectOfficePhone = (extension?: string | null, phoneNumber?: string | null): string => {
    const directOfficePhone = phoneNumber
      ? parsePhoneNumberFromString(userProfile.directOfficePhoneNumber, userProfile.countryCode)
      : null;
    if (directOfficePhone) {
      if (extension && phoneNumber) {
        return `${directOfficePhone?.formatInternational()} ext. ${getDisplayValue(extension)}`.trim();
      }
      if (directOfficePhone) {
        return directOfficePhone.formatInternational();
      }
    }
    return 'Not Specified'; // Both are missing
  };

  const getCellPhone = (cellPhone: string | null) => {
    const phone = cellPhone ? parsePhoneNumberFromString(cellPhone, userProfile.countryCode) : null;
    if (phone) {
      return phone.formatInternational();
    }
    return 'Not Specified';
  };

  const onEdit = (newProfileInfo: EditableProfile) => {
    dispatch(editUserProfile(newProfileInfo)).then((result) => {
      if (editUserProfile.fulfilled.match(result)) {
        setShowSnackbarMsg({
          msg: 'Successfully updated the profile!',
          type: 'success',
        });
        dispatch(
          setCurrentUser({
            user: {
              ...currentUser,
              ...newProfileInfo,
            },
            accessToken: currentUser.accessToken,
          }),
        );
        dispatch(getUserProfile());
      }
      if (editUserProfile.rejected.match(result)) {
        setShowSnackbarMsg({
          msg: 'Something went wrong when updating the profile!',
          type: 'error',
        });
      }
      setShowEditDialog(false);
    });
  };

  const onEmailChange = (newEmailInfo: { password: string; newEmail: string }) => {
    dispatch(askEmailChange(newEmailInfo)).then((result) => {
      if (askEmailChange.fulfilled.match(result)) {
        setShowSnackbarMsg({
          msg: 'Verification link sent! Please check your email!',
          type: 'success',
        });
      }
      if (askEmailChange.rejected.match(result)) {
        setShowSnackbarMsg({
          msg:
            result.payload === 'Wrong password'
              ? 'Current password not recognized. Please try again or reset password'
              : (result.payload as string),
          type: 'error',
        });
      }
    });
  };

  const onPasswordReset = () => {
    dispatch(askPasswordReset()).then((result) => {
      if (askPasswordReset.fulfilled.match(result)) {
        setShowSnackbarMsg({
          msg: 'Please check your email and click the link provided to reset your password!',
          type: 'success',
        });
      }

      if (askPasswordReset.rejected.match(result)) {
        setShowSnackbarMsg({
          msg: result.payload as string,
          type: 'error',
        });
      }
    });
  };

  const sections = [
    [
      {
        icon: <PermIdentity sx={{ marginRight: 1 }} />,
        title: 'Name',
        content: `${getDisplayValue(userProfile.firstName)} ${getDisplayValue(userProfile.lastName)}`,
        editPath: '/settings/name',
      },
      {
        icon: <MailOutline sx={{ marginRight: 1 }} />,
        title: 'Email',
        content: getDisplayValue(userProfile.email),
        editPath: '/settings/email',
      },
      {
        icon: <LockOutlined sx={{ marginRight: 1 }} />,
        title: 'Cell Phone',
        content: getCellPhone(userProfile.cellPhoneNumber),
        editPath: '/settings/cell-phone',
      },
    ],
    [
      {
        icon: <LockOutlined sx={{ marginRight: 1 }} />,
        title: 'Company Name',
        content: getDisplayValue(userProfile.organization.name),
        editPath: '/settings/company-name',
      },
      {
        icon: <LockOutlined sx={{ marginRight: 1 }} />,
        title: 'Password',
        content: relevantTimeAgo(currentUser.passwordUpdatedAt),
        editPath: '/settings/password',
      },
      {
        icon: <LockOutlined sx={{ marginRight: 1 }} />,
        title: 'Direct Office Phone',
        content: getDirectOfficePhone(
          userProfile.directOfficePhoneNumberExtension,
          userProfile.directOfficePhoneNumber,
        ),
        editPath: '/settings/direct-office-phone',
      },
    ],
  ];

  return (
    <Box sx={{ padding: '1.5rem' }}>
      <Box sx={{ display: 'flex', gap: '1.5rem' }}>
        {sections.map((column, columnIndex) => (
          <Box key={columnIndex} sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', minWidth: '25rem' }}>
            {column.map((section) => (
              <Section
                key={section.title}
                icon={section.icon}
                title={section.title}
                content={[section.content]}
                onEdit={() => navigate(section.editPath)}
              />
            ))}
          </Box>
        ))}
      </Box>
      <Box sx={{ maxWidth: '18.75rem', marginTop: '1.5rem' }}>
        <Button type="outline" onClick={() => setShowEditDialog(true)}>
          <EditOutlined />
          <Spacer w={8} />
          Edit Profile
        </Button>
      </Box>
      <EditProfileData
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSubmit={onEdit}
        onEmailChange={onEmailChange}
        onPasswordReset={onPasswordReset}
        info={{
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          email: userProfile.email,
          countryCode: userProfile.countryCode
            ? (Object.keys(countryPhoneInfo).find(
                (key) => key === userProfile.countryCode,
              ) as keyof typeof countryPhoneInfo)
            : 'US',
          cellPhoneNumber: userProfile.cellPhoneNumber
            ? format(userProfile.cellPhoneNumber, {
                mask: countryPhoneInfo[userProfile.countryCode as keyof typeof countryPhoneInfo].mask,
                replacement: { '_': /\d/ },
              })
            : '',
          directOfficePhoneNumber: userProfile.directOfficePhoneNumber
            ? format(userProfile.directOfficePhoneNumber, {
                mask: countryPhoneInfo[userProfile.countryCode as keyof typeof countryPhoneInfo].mask,
                replacement: { '_': /\d/ },
              })
            : '',
          directOfficePhoneNumberExtension: userProfile.directOfficePhoneNumberExtension || '',
        }}
      />
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
    </Box>
  );
};

export default ProfileTab;
